import { css, html, svg } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { BaseElement } from '../base-element.ts';
import defaultLogoUrl from './assets/dur-steel.svg';
import awardStartUrl from './assets/award-star.svg';
import salinityUrl from './assets/salinity.svg';
import ecoUrl from './assets/eco.svg';
import floristUrl from './assets/florist.svg';

export interface SteelCardMetric {
  icon: string;
  /** Rendered as raw HTML (e.g. to allow <br>), so only pass trusted content. */
  label: string;
}

const ICONS: Record<string, string> = {
  award: awardStartUrl,
  salinity: salinityUrl,
  eco: ecoUrl,
  florist: floristUrl,
};

const DEFAULT_ICON = svg`
  <circle cx="12" cy="12" r="8" />
`;

@customElement('dur-steel-card')
export class DurSteelCard extends BaseElement {
  static styles = [
    BaseElement.styles,
    css`
      :host {
        display: block;
      }

      .card {
        display: flex;
        justify-content: center;
        padding: 20.608px 19.396px;
        border-radius: 8px;
        color: #fff;
        text-align: center;
        background: #383D5C;

        @media (min-width: 768px) {
          padding: 18.666px 17.568px;
        }

        @media (min-width: 1024px) {  
          padding: 34px 32px;
        }
      }

      .logo-area {
        display: flex;
        align-items: center;
        flex-shrink: 0;
      }

      .logo {
        max-width: 43.792px;
        max-height: 30.201px;

        @media (min-width: 768px) {
          max-width: 39.665px;
          max-height: 27.355px;
        }

        @media (min-width: 768px) {
          max-width: 72px;
          max-height: 50px;
        }
      }

      .logo--fallback {
        font-size: var(--text-lg, 18px);
        font-weight: var(--font-weight-light, 300);
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }

      .divider {
        align-self: stretch;
        width: 1.212px;
        margin: 0 19px;
        background: #fff;

        @media (min-width: 768px) {
          margin: 0 18px;
        }

        @media (min-width: 1024px) { 
          width: 2px; 
          margin: 0 32px;
        }
      }

      .metrics {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: flex-start;
        gap: 12px 24px;
        list-style: none;
        margin: 0;
        padding: 0;

        @media (min-width: 768px) { 
          gap: 12px 21px;
        }

        @media (min-width: 1024px) {
          gap: 20px 40px;
        }
      }

      .metric {
        display: flex;
        flex: 1 1 0;
        min-width: 0;
        flex-direction: column;
        align-items: center;
        gap: 3px;

        @media (min-width: 768px) {
          gap: 2.7px;
        }

        @media (min-width: 1024px) {
          gap: 5px;
        }
      }

      .icon {
        width: 15.7px;
        height: 15.7px;

        @media (min-width: 768px) {
          width: 13.176px;
          height: 13.176px;
        }

        @media (min-width: 1024px) {
          width: 24px;
          height: 24px;
        }
      }

      .metric-label {
        color: #FFF;
        text-align: center;
        font-family: Lato;
        font-size: 6.061px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        text-transform: uppercase;
        min-width: 0;
        max-width: 100%;

        @media (min-width: 768px) {
          font-size: 5.49px;
        }

        @media (min-width: 1024px) {
          font-size: 10px;
        }
      }
    `,
  ];

  @property({ type: String, attribute: 'logo-src' }) logoSrc = '';
  @property({ type: String, attribute: 'logo-alt' }) logoAlt = '';
  @property({ type: String }) background = '#383D5C';
  @property({ type: Array }) metrics: SteelCardMetric[] = [];

  render() {
    const logoSrc = this.logoSrc || defaultLogoUrl;

    return html`
      <div class="card" style="background: ${this.background}">
        <div class="logo-area">
          ${html`<img class="logo" src=${logoSrc} alt=${this.logoAlt} />`}
          ${this.metrics.length ? html`<div class="divider"></div>` : ''}
        </div>

        <ul class="metrics">
          ${map(
            this.metrics,
            (metric) => html`
              <li class="metric">
                ${html`<img class="icon" src=${ICONS[metric.icon] ?? DEFAULT_ICON}  />`}
                <span class="metric-label">${unsafeHTML(metric.label)}</span>
              </li>
            `
          )}
        </ul>
      </div>
    `;
  }
}
